import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Admin.css';
import { Plan, Settings } from './types';
import AddPlanModal from './AddPlanModal';
import EditPlanModal from './EditPlanModal';
import UsersTab from './UsersTab';
import { useRole } from '../../hooks/useRole';
import CodeMirror from '@uiw/react-codemirror';
import { handlebarsLanguage } from "@xiechao/codemirror-lang-handlebars"

const Admin = () => {
    const { hasRole } = useRole();
    const [settings, setSettings] = useState<Settings | null>(null);
    const [isAddPlanModalOpen, setIsAddPlanModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
    const [activeTab, setActiveTab] = useState<'settings' | 'plans' | 'users'>('settings');

    const getSettings = async () => {
        fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/get-settings`, {
            headers: {
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            }
        }).then(response => response.json()).then((data: Settings) => {
            setSettings(data);
        });
    }

    const updateSettings = async () => {
        if (!settings) {
            toast.error("No settings to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (!settings.basicAssistantSettings) {
            toast.error("No basic assistant settings to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (!settings.modelGenerationPrompt) {
            toast.error("No model generation prompt to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (!settings.chatSessionStripeId) {
            toast.error("No chat session stripe ID to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (!settings.chatSessionPrice) {
            toast.error("No chat session price to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (!settings.subscriptions) {
            toast.error("No subscriptions to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (settings.subscriptions.length === 0) {
            toast.error("No subscriptions to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (settings.subscriptions.some(sub => !sub.planId)) {
            toast.error("No plan ID to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (settings.basicAssistantSettings.avatarName === '') {
            toast.error("No avatar name to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (settings.basicAssistantSettings.avatarUrl === '') {
            toast.error("No avatar URL to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (settings.basicAssistantSettings.voice === '') {
            toast.error("No voice to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (settings.basicAssistantSettings.welcomeMessage === '') {
            toast.error("No welcome message to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (settings.basicAssistantSettings.chunkOverlap === 0) {
            toast.error("No chunk overlap to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (settings.basicAssistantSettings.maxChunkSize === 0) {
            toast.error("No max chunk size to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (settings.basicAssistantSettings.maxTokenCount === 0) {
            toast.error("No max token count to update.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }

        const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/set-settings`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
            method: "POST",
            body: JSON.stringify(settings),
        });
        if (response.ok) {
            toast.success("Settings updated successfully!", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        } else {
            toast.error("Failed to update settings.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        }
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            await updateSettings();
        } catch (error) {
            toast.error("An error occurred while updating settings.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        }
    };

    const handleAddPlan = async (planData: Plan, createInStripe: boolean) => {
        try {
            if (createInStripe) {
                const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/create-plan`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`,
                    },
                    body: JSON.stringify({
                        plan: planData,
                        createInStripe
                    }),
                });

                if (response.ok) {
                    toast.success("Plan added successfully", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
                } else {
                    toast.error("Error adding plan", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
                }
            }
            else {
                if (!settings) return;
        
                setSettings({
                    ...settings,
                    subscriptions: settings.subscriptions.map(p => p.planId == planData.planId ? planData : p)
                });

                await updateSettings();
            }
            await getSettings();
            setIsAddPlanModalOpen(false);
        } catch (error) {
            toast.error("Error adding plan", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        }
    };

    const handleEditPlan = (plan: Plan) => {
        setEditingPlan(plan);
    };

    const handleSavePlan = async (planData: Plan) => {
        try {
            if (!settings) return;
        
            const s = settings;
            s.subscriptions = s.subscriptions.map(p => p.planId == planData.planId ? planData : p)
            setSettings(s);

            await updateSettings();
            setEditingPlan(null);
        } catch (error) {
            toast.error("Error updating plan", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        }
    };

    useEffect(() => {
        getSettings();
        if (hasRole('admin')) {
            setActiveTab('users');
        }
    }, []);

    return (
        <div className="admin-container">
            {hasRole('admin') && (
                <>
                    <div className="admin-tabs-nav">
                        {hasRole('superadmin') && (
                        <button 
                            onClick={() => setActiveTab('settings')}
                            className={activeTab === 'settings' ? 'active' : ''}
                        >
                            Settings
                        </button>
                        )}
                        {hasRole('superadmin') && (
                        <button 
                            onClick={() => setActiveTab('plans')}
                            className={activeTab === 'plans' ? 'active' : ''}
                        >
                            Plans
                        </button>
                        )}
                        {hasRole('admin') && (
                        <button 
                            onClick={() => setActiveTab('users')}
                                className={activeTab === 'users' ? 'active' : ''}
                            >
                                Users
                            </button>
                        )}
                    </div>

                    {activeTab === 'settings' && (
                        <section className="settings-section">
                            <h2>Settings</h2>
                            <form onSubmit={handleSubmit}>
                                <div className="codemirror-info">
                                    <p>Available variables for use in templates (use double curly braces, e.g. <code>{'{'}{'{'}welcomeMessage{'}'}{'}'}</code>):</p>
                                    <ul>
                                        <li><code>greetingsEnabled</code></li>
                                        <li><code>welcomeMessage</code></li>
                                        <li><code>brandDescription</code></li>
                                        <li><code>contactInfo</code></li>
                                        <li><code>extraInfo</code></li>
                                        <li><code>popularProducts</code></li>
                                        <li><code>categories</code></li>
                                    </ul>
                                </div>
                                <div className="codemirror-container">
                                    <label>Base Assistant Prompt (3D Template):</label>
                                    <CodeMirror
                                        value={settings?.basicAssistantSettings?.prompt3DTemplate || ''}
                                        height="200px"
                                        extensions={[handlebarsLanguage]}
                                        onChange={(value) => 
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings, 
                                                basicAssistantSettings: { 
                                                    ...prevSettings.basicAssistantSettings,
                                                    prompt3DTemplate: value
                                                } 
                                            }) : null)
                                        }
                                    />
                                </div>
                                <div className="codemirror-container">
                                    <label>Base Assistant Prompt (2D Template):</label>
                                    <CodeMirror
                                        value={settings?.basicAssistantSettings?.prompt2DTemplate || ''}
                                        height="200px"
                                        extensions={[handlebarsLanguage]}
                                        onChange={(value) => 
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings, 
                                                basicAssistantSettings: { 
                                                    ...prevSettings.basicAssistantSettings,
                                                    prompt2DTemplate: value
                                                } 
                                            }) : null)
                                        }
                                    />
                                </div>
                                <div>
                                    <label>Avatar Name:</label>
                                    <input
                                        type="text"
                                        value={settings?.basicAssistantSettings?.avatarName || ''}
                                        onChange={(e) => 
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings, 
                                                basicAssistantSettings: { 
                                                    ...prevSettings.basicAssistantSettings,
                                                    avatarName: e.target.value 
                                                } 
                                            }) : null)
                                        }
                                    />
                                </div>
                                <div>
                                    <label>Avatar URL:</label>
                                    <input
                                        type="text"
                                        value={settings?.basicAssistantSettings?.avatarUrl || ''}
                                        onChange={(e) => 
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings, 
                                                basicAssistantSettings: { 
                                                    ...prevSettings.basicAssistantSettings,
                                                    avatarUrl: e.target.value 
                                                } 
                                            }) : null)
                                        }
                                    />
                                </div>
                                <div>
                                    <label>Voice:</label>
                                    <select
                                        value={settings?.basicAssistantSettings?.voice || ''}
                                        onChange={(e) => 
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings, 
                                                basicAssistantSettings: { 
                                                    ...prevSettings.basicAssistantSettings,
                                                    voice: e.target.value 
                                                } 
                                            }) : null)
                                        }
                                    >
                                        <option value="alloy">alloy</option>
                                        <option value="ash">ash</option>
                                        <option value="ballad">ballad</option>
                                        <option value="coral">coral</option>
                                        <option value="echo">echo</option>
                                        <option value="fable">fable</option>
                                        <option value="onyx">onyx</option>
                                        <option value="nova">nova</option>
                                        <option value="sage">sage</option>
                                        <option value="shimmer">shimmer</option>
                                    </select>
                                </div>
                                <div>
                                    <label>Welcome Message:</label>
                                    <textarea
                                        value={settings?.basicAssistantSettings?.welcomeMessage || ''}
                                        onChange={(e) => 
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings, 
                                                basicAssistantSettings: { 
                                                    ...prevSettings.basicAssistantSettings,
                                                    welcomeMessage: e.target.value 
                                                } 
                                            }) : null)
                                        }
                                        rows={3}
                                        style={{ width: '100%', fontFamily: 'monospace' }}
                                    />
                                </div>
                                <div>
                                    <label>Chunk Overlap:</label>
                                    <input
                                        type="number"
                                        value={settings?.basicAssistantSettings?.chunkOverlap !== undefined ? String(settings.basicAssistantSettings.chunkOverlap) : ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings,
                                                basicAssistantSettings: {
                                                    ...prevSettings.basicAssistantSettings,
                                                    chunkOverlap: value === '' ? 0 : parseInt(value, 10) 
                                                }
                                            }) : null)
                                        }}
                                    />
                                </div>
                                <div>
                                    <label>Max Chunk Size:</label>
                                    <input
                                        type="number"
                                        value={settings?.basicAssistantSettings?.maxChunkSize !== undefined ? String(settings.basicAssistantSettings.maxChunkSize) : ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings,
                                                basicAssistantSettings: {
                                                    ...prevSettings.basicAssistantSettings,
                                                    maxChunkSize: value === '' ? 0 : parseInt(value, 10) 
                                                }
                                            }) : null)
                                        }}
                                    />
                                </div>
                                <div>
                                    <label>Max Token Count:</label>
                                    <input
                                        type="text"
                                        value={settings?.basicAssistantSettings?.maxTokenCount !== undefined ? String(settings.basicAssistantSettings.maxTokenCount) : ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || /^[0-9]+$/.test(value)) {
                                                setSettings(prevSettings => prevSettings ? ({ 
                                                    ...prevSettings,
                                                    basicAssistantSettings: {
                                                        ...prevSettings.basicAssistantSettings,
                                                        maxTokenCount: value === '' ? 0 : parseInt(value, 10) 
                                                    }
                                                }) : null)
                                            }
                                        }}
                                    />
                                </div>
                                <div>
                                    <label>Model Generator Prompt:</label>
                                    <textarea
                                        value={settings?.modelGenerationPrompt || ''}
                                        onChange={(e) => 
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings, 
                                                modelGenerationPrompt: e.target.value 
                                            }) : null)
                                        }
                                        rows={3}
                                        style={{ width: '100%', fontFamily: 'monospace' }}
                                    />
                                </div>
                                <div>
                                    <label>Chat Session Stripe ID:</label>
                                    <input
                                        type="text"
                                        value={settings?.chatSessionStripeId || ''}
                                        onChange={(e) => 
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings, 
                                                chatSessionStripeId: e.target.value 
                                            }) : null)
                                        }
                                    />
                                </div>
                                <div>
                                    <label>Chat Session Price (in cents):</label>
                                    <input
                                        type="number"
                                        value={settings?.chatSessionPrice !== undefined ? String(settings.chatSessionPrice) : ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings,
                                                chatSessionPrice: value === '' ? 0 : parseInt(value, 10) 
                                            }) : null)
                                        }}
                                    />
                                </div>
                                <div className="checkbox-field-container">
                                    <label>One Product Per File:</label>
                                    <input
                                        className="admin-checkbox"
                                        type="checkbox"
                                        checked={settings?.basicAssistantSettings?.oneProductPerFile || false}
                                        onChange={(e) => 
                                            setSettings(prevSettings => prevSettings ? ({ 
                                                ...prevSettings,
                                                basicAssistantSettings: {
                                                    ...prevSettings.basicAssistantSettings,
                                                    oneProductPerFile: e.target.checked
                                                }
                                            }) : null)
                                        }
                                    />
                                </div>
                                <button type="submit">Update Settings</button>
                            </form>
                        </section>
                    )}
                    
                    {activeTab === 'plans' && hasRole('superadmin') && (
                        <section className="plans-section">
                            <h2>Plans Settings</h2>
                            <button 
                                onClick={() => setIsAddPlanModalOpen(true)}
                                className="add-button"
                            >
                                Add new plan
                            </button>
                            <div className="plans-list">
                                <table className="plans-table">
                                    <thead>
                                        <tr>
                                            <th>Plan ID</th>
                                            <th>Stripe ID</th>
                                            <th>Credits</th>
                                            <th>HD Generation</th>
                                            <th>Chat Sessions</th>
                                            <th>Price</th>
                                            <th>Period</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {settings?.subscriptions?.map((plan: Plan) => (
                                            <tr key={plan.planId}>
                                                <td>{plan.planId}</td>
                                                <td>{plan.stripeId}</td>
                                                <td>{plan.limits.creditsAvailable}</td>
                                                <td>{plan.limits.HDGenerationAvailable ? 'Yes' : 'No'}</td>
                                                <td>{plan.limits.chatSessionsAvailable}</td>
                                                <td>{plan.price / 100} $</td>
                                                <td>{plan.period}</td>
                                                <td>
                                                    <button 
                                                        onClick={() => handleEditPlan(plan)}
                                                        className="edit-button"
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {activeTab === 'users' && (
                        <UsersTab />
                    )}
                </>
            )}
            {isAddPlanModalOpen && (
                <AddPlanModal
                    onSave={handleAddPlan}
                    onClose={() => setIsAddPlanModalOpen(false)}
                />
            )}

            {editingPlan && (
                <EditPlanModal
                    plan={editingPlan}
                    onSave={handleSavePlan}
                    onClose={() => setEditingPlan(null)}
                />
            )}
            <ToastContainer />
        </div>
    );
};

export default Admin;